import Vue from 'vue'
import store from './store'
import VueApollo from 'vue-apollo'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'

import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
import './sass/_variables.sass'

import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { createHttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { onError } from 'apollo-link-error'

// Copy Clipboard
import VueClipboard from 'vue-clipboard2'

// Ant design dependencies
// import Steps from 'ant-design-vue/lib/steps'
import { Timeline, Steps, Icon as AntIcon, Modal, Result, Button, Input } from 'ant-design-vue'
import 'ant-design-vue/dist/antd.css'

// Font-awesome
import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from '@fortawesome/vue-fontawesome'

import Chart from 'chart.js'
import i18n from './i18n'

import App from './App.vue'
import './registerServiceWorker'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'

import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'dripicons/webfont/webfont.css'
import '@mdi/font/css/materialdesignicons.min.css'

import '@/filters/filters.js'
import AxiosSetup from '@/helpers/AxiosSetup'
import { generateRoutes } from '@/helpers/LmDynamicRoutes'
import { EventBus } from '@/event-bus.js'
import VueSocketIO from 'vue-socket.io'
import axios from 'axios'

import LmBreadcrumb from './components/LmBreadcrumb.vue'
import LmStockGraph from './components/LmStockGraph.vue'
import LmChartBar from './components/charts/LmChartBar.vue'
import LmChartLine from './components/charts/LmChartLine.vue'
import LmChartPie from './components/charts/LmChartPie.vue'
import LmTable from './components/LmTable.vue'
import LmSubTitle from './components/LmSubTitle.vue'
import LmSimpleCard from './components/LmSimpleCard.vue'
import LmCardMore from './components/LmCardMore.vue'
import LmComponentLoading from './components/LmComponentLoading.vue'
import Vuelidate from 'vuelidate'
import PortalVue from 'portal-vue'
import VueCrontab from 'vue-crontab'

// Svg Icons
import LmSvgIcon from '@/svg/LmSvgIcon'

// https://github.com/mycurelabs/vue-html-to-paper
import VueHtmlToPaper from 'vue-html-to-paper'

import Rollbar from 'rollbar'

// Vue Scroll Chat
import VueChatScroll from 'vue-chat-scroll'

// Browser Detection https://github.com/ICJIA/vue-browser-detect-plugin
// https://app.clickup.com/t/314jkt9
import browserDetect from 'vue-browser-detect-plugin'

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
      if (message === 'Unauthorized') document.location.href = '/login'
    }
    )
    // document.location.href = '/login'
  }
  if (networkError) console.log(`[Network error]: ${networkError}`)
})
Vue.use(VueApollo)
// HTTP connection to the API
const httpLink = createHttpLink({
  // You should use an absolute URL here
  uri: `${process.env.VUE_APP_GRAPHQL_URL}`,
  credentials: 'include'
})

const link = ApolloLink.from([
  errorLink,
  httpLink
])

// Cache implementation
const cache = new InMemoryCache({
  addTypename: false
})

// Browser Detection
Vue.use(browserDetect)

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  }
}

// Create the apollo client
const apolloClient = new ApolloClient({
  link,
  cache: cache,
  defaultOptions: defaultOptions
})
const apolloProvider = new VueApollo({
  defaultClient: apolloClient
})

// Socket.io
if (process.env.VUE_APP_SOCKET_ENABLED === 'yes') {
  Vue.use(new VueSocketIO({
    debug: false,
    connection: `${process.env.VUE_APP_SOCKET_CONNECTION}`,
    vuex: {
      store,
      actionPrefix: 'SOCKET_',
      mutationPrefix: 'SOCKET_'
    },
    options: { path: process.env.VUE_APP_SOCKET_PATH } // Optional options
  }))
}

// Container Copy Clipboard
VueClipboard.config.autoSetContainer = true

const options = {
  name: '_blank',
  specs: [
    'fullscreen=yes',
    'titlebar=yes',
    'scrollbars=yes'
  ],
  styles: [
    'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
    'https://unpkg.com/kidlat-css/css/kidlat.css',
    '/css/print.css'
  ]
}

Vue.use(VueHtmlToPaper, options)
Vue.use(PortalVue)

Chart.defaults.global.defaultFontColor = '#6c757d'
Chart.defaults.global.defaultFontFamily = 'Nunito,sans-serif'
Chart.defaults.global.defaultFontSize = 14
Chart.defaults.global.defaultFontStyle = 'normal'
Chart.defaults.global.legend.position = 'bottom'
Chart.defaults.global.title.fontSize = 16
Chart.defaults.global.title.fontStyle = 'bold'
Chart.defaults.global.title.padding = 0

Vue.component('LmBreadcrumb', LmBreadcrumb)
Vue.component('LmStockGraph', LmStockGraph)
Vue.component('LmChartBar', LmChartBar)
Vue.component('LmChartLine', LmChartLine)
Vue.component('LmChartPie', LmChartPie)
Vue.component('LmTable', LmTable)
Vue.component('LmSubTitle', LmSubTitle)
Vue.component('LmSimpleCard', LmSimpleCard)
Vue.component('LmCardMore', LmCardMore)
Vue.component('LmComponentLoading', LmComponentLoading)
// Ant Design Components
Vue.component('Steps', Steps)
// Marker cluster to leaflet
Vue.component('v-marker-cluster', Vue2LeafletMarkerCluster)
// Copy Clipboard
Vue.component('VueClipboard', VueClipboard)

// Font awesome

Vue.component('font-awesome-icon', FontAwesomeIcon)
Vue.component('font-awesome-layers', FontAwesomeLayers)
Vue.component('font-awesome-layers-text', FontAwesomeLayersText)

// Svg Icons insert to Vue
Vue.component('LmSvgIcon', LmSvgIcon)

Vue.config.productionTip = false

AxiosSetup()

Vue.use(BootstrapVue)
Vue.use(BootstrapVueIcons)
Vue.use(Vuelidate)

// Ant Design Components
Vue.use(Steps)
Vue.use(Timeline)
Vue.use(AntIcon)
Vue.use(Modal)
Vue.use(Result)
Vue.use(Button)
Vue.use(Input)

// Copy Clipboard
Vue.use(VueClipboard)

Vue.use(VueChatScroll)
Vue.use(VueCrontab)

// Rollbar logging
if (process.env.VUE_APP_ROLLBAR_ENABLE === 'yes' && process.env.NODE_ENV !== 'development') {
  Vue.prototype.$rollbar = new Rollbar({
    accessToken: process.env.VUE_APP_ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    enabled: true,
    environment: process.env.NODE_ENV,
    ignoredMessages: [
      'Request failed with status code 401'
    ],
    payload: {
      client: {
        javascript: {
          code_version: '2.0.0',
          source_map_enabled: true,
          guess_uncaught_frames: true
        }
      }
    }
  })

  Vue.config.errorHandler = function (err, vm, info) {
    vm.$rollbar.error(err)
    throw err // rethrow
  }
}

Vue.config.productionTip = false

// this part resolve an issue where the markers would not appear
delete Icon.Default.prototype._getIconUrl

Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Import condicional con babel
// https://2ality.com/2017/01/import-operator.html
// Comento el "await import" ya que tira un warning de websocket
// TODO. Importar con "require" no necesita async/await
async function loadRouter () {
  try {
    let router
    if (process?.env?.VUE_APP_DYNAMIC_ROUTER === 'yes') {
      // router = await import('./dynamicRouter')
      router = require('./dynamicRouter')
    } else {
      // router = await import('./router')
      router = require('./router')
    }
    return router.createRouter
  } catch (error) {
    console.log('Error al importar router', error)
  }
}

const main = async () => {
  const createRouter = await loadRouter()
  const router = await createRouter()

  EventBus.$on('routePush', (destination) => {
    setTimeout(() => {
      if (window.location.pathname !== `/${destination.name}`) {
        router.push(destination).catch(err => {
          console.log('err', err)
        })
      }
    }, 1)
  })

  router.beforeEach(async (to, from, next) => {
    if (to && to.query && to.query.token) {
      const token = `${to.query.token}`
      let nextPath = '/'

      const params = new URLSearchParams()
      if (to.query) {
        Object.keys(to.query).forEach((key) => {
          if (key === 'token') {
            return
          }
          const value = to.query[key]
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, `${v}`))
          } else if (typeof value !== 'undefined' && value !== null) {
            params.append(key, `${value}`)
          }
        })
      }

      const qsWithoutToken = params.toString()
      const cleanedCurrentUrl = `${to.path}${qsWithoutToken ? `?${qsWithoutToken}` : ''}`

      // Remuevo el token de la URL lo antes posible (evita que quede en el historial/copypaste)
      try {
        window.history.replaceState({}, document.title, cleanedCurrentUrl)
      } catch (e) {
        // ignore
      }

      if (to.path === '/login' && to.query && to.query.redirect) {
        nextPath = to.query.redirect
      } else {
        nextPath = cleanedCurrentUrl
      }

      try {
        const consumeResp = await axios.get('/usuarios/sso/consume', {
          params: {
            token,
            next: nextPath,
            mode: 'json'
          },
          headers: {
            Accept: 'application/json'
          }
        })

        if (!consumeResp || !consumeResp.data || consumeResp.data.ok !== true) {
          const errorCode = consumeResp && consumeResp.data && consumeResp.data.errorCode
            ? consumeResp.data.errorCode
            : 'SSO_ERROR'
          next({ path: '/sso-error', query: { code: errorCode } })
          return
        }

        // En modo session, la autenticacion real queda en la cookie.
        // Vuex necesita quedar en isAuthenticated=true para que el guard/dynamic router funcionen.
        if (process.env.VUE_APP_AUTH_MODE === 'session') {
          store.commit('session/mutateAuthenticated', {
            accessToken: 'token',
            idToken: '1',
            expiresIn: 2 * 60 * 60
          })
        }

        await store.dispatch('session/getUserInfo')

        if (process?.env?.VUE_APP_DYNAMIC_ROUTER === 'yes') {
          const routes = await generateRoutes()
          routes.forEach(route => {
            router.addRoute(route)
          })
        }

        next(nextPath)
        return
      } catch (error) {
        const errorCode = error && error.response && error.response.data && error.response.data.errorCode
          ? error.response.data.errorCode
          : 'SSO_ERROR'
        next({ path: '/sso-error', query: { code: errorCode } })
        return
      }
    }

    let conditionalRoute = to.meta.conditionalRoute
    let pathConditionalRoute = to.meta.pathConditionalRoute

    if (conditionalRoute === 'false' || conditionalRoute === 'no') conditionalRoute = false

    if (!pathConditionalRoute) pathConditionalRoute = '/'

    document.title = `${to.meta.title ? to.meta.title + ' -' : ''}  ${store.getters['settings/appName']} `
    if (store.getters['session/isAuthenticated'] && store.getters['notifications/unreadNotifications']) {
      document.title = `(${store.getters['notifications/unreadNotifications']}) ${document.title}`
    }

    if (conditionalRoute === false) {
      next({ path: pathConditionalRoute })
      return
    }

    next()
  })

  new Vue({
    router,
    store,
    i18n,
    apolloProvider,
    errorCaptured (error, component, info) {
      console.log('errorCaptured error', error + component + info)
    },
    render: h => h(App)
  }).$mount('#app')
}

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('beforeinstallprompt')
})

main()
